import React, { useEffect, useState } from 'react';

import { Box, Button, Tooltip } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';

import { CompanyRoleDTO } from '@vyce/core/src/api/types';
import { requiresResourceSelection } from '@vyce/core/src/utils/staffPermissions';
import { StaffListItem } from '@vyce/core/src/types';
import { ButtonTitleWithLoading } from '@vyce/core/src/components/ButtonTitleWithLoading';
import { ConfirmDialog } from '@vyce/core/src/components/ConfirmDialog';

import { useCompanyRolesEditor } from '../hooks/useCompanyRolesEditor';
import { useRegisterRolesEditor } from '../hooks/useRegisterRolesEditor';
import { useRolesStyles } from '../styles';
import { ModuleRoleRow } from './ModuleRoleRow';

interface Props {
  companyId?: string;
  userId: string;
  userLastName?: string;
  /** Pass it when the caller already holds the staff row — saves 1–2 requests. */
  staffItem?: StaffListItem;
  enabled: boolean;
  /** Identity in the page's Save bar. Defaults to companyId:userId. */
  editorId?: string;
  /** What the page's Save bar calls this editor — the host knows the name. */
  label?: string;
  onSummaryChange?: (assigned: number, total: number) => void;
}

export const CompanyRolesEditor: React.FC<Props> = ({
  companyId,
  userId,
  userLastName,
  staffItem,
  enabled,
  editorId,
  label,
  onSummaryChange,
}) => {
  const classes = useRolesStyles();
  const [expandedModuleKey, setExpandedModuleKey] = useState<string | null>(null);
  const [modulesWithExceptions, setModulesWithExceptions] = useState<Record<string, boolean>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const {
    status,
    canEdit,
    isOwner,
    visibleModules,
    draft,
    baseline,
    permissions,
    effectiveByModule,
    catalogue,
    saving,
    isDirty,
    invalidReason,
    setModuleRole,
    toggleModuleResource,
    toggleAllModuleResources,
    togglePermission,
    resetModulePermissions,
    setClearResourceOverrides,
    discard,
    save,
  } = useCompanyRolesEditor({ companyId, userId, userLastName, staffItem, enabled });

  // "Apply to all" wipes a module's per-resource exceptions, so warn before it does.
  // Computed above the early returns — the page Save bar needs it to build one
  // confirmation for the whole page.
  const clearsExistingExceptions = visibleModules.some(
    module =>
      modulesWithExceptions[module.systemName] && permissions[module.systemName]?.clearResourceOverrides
  );

  // With a page-level Save bar mounted, this editor hands over its Save, Discard
  // and confirmation dialog and renders neither.
  const hasPageSaveBar = useRegisterRolesEditor({
    id: editorId || `${companyId || 'none'}:${userId}`,
    label: label || '',
    isDirty,
    canEdit,
    saving,
    invalidReason,
    needsExceptionsConfirm: isDirty && clearsExistingExceptions,
    save,
    discard,
  });

  // Feed the collapsed row's "N of M modules assigned" subtitle from the
  // saved state (baseline), so it doesn't jump around while editing.
  useEffect(() => {
    if (status !== 'ready' || !onSummaryChange) return;
    const assigned = visibleModules.filter(module => !!baseline[module.systemName]?.role).length;
    onSummaryChange(assigned, visibleModules.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, baseline, visibleModules]);

  if (status === 'idle' || status === 'loading') {
    return (
      <Box paddingX={2.5} paddingBottom={2.5}>
        {[0, 1, 2].map(index => (
          <Box key={index} marginBottom={0.75}>
            <Skeleton variant="rect" height={52} style={{ borderRadius: 12 }} />
          </Box>
        ))}
      </Box>
    );
  }

  if (status === 'error') {
    return <div className={classes.readonlyCaption}>Failed to load permissions for this company.</div>;
  }

  if (status === 'readonly') {
    return (
      <div className={classes.readonlyCaption}>
        This user isn't on the staff list of this company, so permissions can't be edited here.
      </div>
    );
  }

  const handleRoleChange = (moduleKey: string, role: CompanyRoleDTO | null) => {
    setModuleRole(moduleKey, role);
    // A role scoped to resources needs its picker reviewed straight away.
    if (role && requiresResourceSelection(role)) {
      setExpandedModuleKey(moduleKey);
    } else if (expandedModuleKey === moduleKey) {
      setExpandedModuleKey(null);
    }
  };

  const handleSaveClick = () => {
    if (clearsExistingExceptions) {
      setConfirmOpen(true);
      return;
    }
    save();
  };

  const saveButton = (
    <Button
      size="small"
      variant="contained"
      color="primary"
      disabled={!!invalidReason || saving}
      onClick={handleSaveClick}>
      <ButtonTitleWithLoading title="Save" loaderVariant="paper" loading={saving} />
    </Button>
  );

  return (
    <div className={classes.moduleList}>
      {status === 'noPosition' && (
        <div className={classes.readonlyCaption}>
          This person holds no position in this company — assign a role first.
        </div>
      )}
      {isOwner && (
        <div className={classes.readonlyCaption}>
          Company owner — full access. Roles and permissions can't be narrowed here.
        </div>
      )}
      {visibleModules
        .filter(module => !!draft[module.systemName] && !!permissions[module.systemName])
        .map(module => (
          <ModuleRoleRow
            key={module.systemName}
            companyId={companyId}
            userId={userId}
            module={module}
            draft={draft[module.systemName]}
            permissionState={permissions[module.systemName]}
            effective={effectiveByModule[module.systemName] || new Set<string>()}
            catalogue={catalogue}
            disabled={!canEdit}
            isOwner={isOwner}
            expanded={expandedModuleKey === module.systemName}
            onToggleExpand={() =>
              setExpandedModuleKey(expandedModuleKey === module.systemName ? null : module.systemName)
            }
            onRoleChange={role => handleRoleChange(module.systemName, role)}
            onResourceToggle={(id, checked) => toggleModuleResource(module.systemName, id, checked)}
            onSelectAllResources={checked => toggleAllModuleResources(module.systemName, checked)}
            onPermissionToggle={(systemName, checked) =>
              togglePermission(module.systemName, systemName, checked)
            }
            onResetPermissions={() => resetModulePermissions(module.systemName)}
            onClearResourceOverridesChange={value => setClearResourceOverrides(module.systemName, value)}
            onExceptionsLoaded={hasExceptions =>
              setModulesWithExceptions(prev =>
                prev[module.systemName] === hasExceptions
                  ? prev
                  : { ...prev, [module.systemName]: hasExceptions }
              )
            }
          />
        ))}
      {isDirty && canEdit && !hasPageSaveBar && (
        <div className={classes.saveBar}>
          {/* One invalid module disables Save for the whole panel, so say which and
              why here: as a tooltip alone it looked like the button did nothing. */}
          {invalidReason && <div className={classes.saveBarError}>{invalidReason}</div>}
          <Box marginRight={1}>
            <Button size="small" disabled={saving} onClick={discard}>
              Discard
            </Button>
          </Box>
          {invalidReason ? (
            <Tooltip title={invalidReason}>
              <span>{saveButton}</span>
            </Tooltip>
          ) : (
            saveButton
          )}
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen && !hasPageSaveBar}
        title="Clear per-location exceptions?"
        subtitle='"Apply to all" replaces every per-resource exception in the changed modules with the permissions above. This can’t be undone.'
        confirmText="Apply to all"
        cancelText="Cancel"
        loading={saving}
        handleClose={() => setConfirmOpen(false)}
        handleConfirm={() => {
          setConfirmOpen(false);
          save();
        }}
      />
    </div>
  );
};
